import {
  Box,
  Flex,
  Image,
  Text,
  VStack,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  IconButton,
  Badge,
  HStack,
} from "@chakra-ui/react";
import {
  LayoutDashboard,
  Users,
  UserCog,
  FileText,
  BarChart2,
  MapPin,
  Share2,
  Package,
  Building2,
  Flag,
  ShoppingCart,
  PhoneCall,
  Puzzle,
  X,
  ChevronRight,
  User,
  MessageCircle,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";

import logo from "../icons/collogo.png";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  interface SigninUser {
    signinuser: {
      UserType: number;
      UserProfile: string;
      menuPermissions: [string];
    };
  }

  const { signinuser } = useSelector(
    (state: { common: SigninUser }) => state.common
  );
  const { menuPermissions } = signinuser || {};
  const location = useLocation();
  const navigate = useNavigate();

  const menu = [
    {
      icon: LayoutDashboard,
      link:
        signinuser?.UserType === 3
          ? "/"
          : signinuser?.UserType === 2
          ? "/admin-dashboard"
          : signinuser?.UserType === 1
          ? "/super-dashboard"
          : "",
      text: "Dashboard",
      badge: null,
    },
    {
      icon: FileText,
      link: "/lead-list",
      text: "Lead List",
      badge: null,
    },
    {
      icon: Users,
      link: signinuser?.UserType === 1 ? "/admin-list" : "/user-list",
      text: signinuser?.UserType === 1 ? "Admin List" : "Team List",
      badge: null,
    },
    {
      icon: UserCog,
      link: "/super-admin-user-list",
      text: "User List",
      badge: null,
    },
    {
      icon: FileText,
      link: "/proposal-list",
      text: "Proposal List",
      badge: null,
    },
    {
      icon: BarChart2,
      link: "/marketing-dashboard",
      text: "Marketing",
      badge: null,
    },
    {
      icon: Share2,
      link: "/source-list",
      text: "Source List",
      badge: null,
    },
    {
      icon: Package,
      link: "/product-list",
      text: "Product List",
      badge: null,
    },
    {
      icon: Building2,
      link: "/branch-list",
      text: "Branch List",
      badge: null,
    },
    {
      icon: MapPin,
      link: "/city-list",
      text: "City List",
      badge: null,
    },
    {
      icon: Flag,
      link: "/status-list",
      text: "Status List",
      badge: null,
    },
    {
      icon: Flag,
      link: "/crm-super-admin-dashboard",
      text: "CRM Website",
      badge: null,
    },
    {
      icon: ShoppingCart,
      link: "/india-mart",
      text: "India Mart",
      badge: null,
    },
    {
      icon: PhoneCall,
      link: "/sidemenu",
      text: "SideMenu",
      badge: null,
    },
    {
      icon: Puzzle,
      link: "/integration",
      text: "Integration",
      badge: null,
    },
    {
      icon: Puzzle,
      link: "/lead-field-config",
      text: "Field Config",
      badge: null,
    },
    {
      icon: Users,
      link: "/admission-dashboard",
      text: "Admission",
      badge: null,
    },
    {
      icon: MessageCircle,
      link: "/dove-soft",
      text: "Dove Soft",
      badge: null,
    },
  ];

  const handleNavigate = (link: string) => {
    navigate(link);
    onClose();
  };

  const filteredMenu = menu?.filter((el) => {
    if (signinuser?.UserType === 1 && signinuser?.UserProfile === "super") {
      return true;
    } else if (menuPermissions && menuPermissions?.includes(el.text)) {
      return true;
    }
    return false;
  });

  const menuItemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i: number) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.05,
        duration: 0.3,
        ease: "easeOut",
      },
    }),
  };

  const MotionFlex = motion(Flex);

  return (
    <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="sm">
      {/* Gray overlay instead of blur */}
      <DrawerOverlay bg="blackAlpha.600" backdropFilter="blur(5px)" />
      <DrawerContent
        maxW="260px"
        bg="white"
        borderTopRightRadius="2xl"
        borderBottomRightRadius="2xl"
        boxShadow="2xl"
      >
        <style>
          {`
            @keyframes gradientShift {
              0% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
              100% { background-position: 0% 50%; }
            }
            
            @keyframes slideInLeft {
              from {
                opacity: 0;
                transform: translateX(-20px);
              }
              to {
                opacity: 1;
                transform: translateX(0);
              }
            }
            
            .gradient-animate {
              background-size: 200% 200%;
              animation: gradientShift 3s ease infinite;
            }
            
            .slide-in-sidebar {
              animation: slideInLeft 0.3s ease-out;
            }
          `}
        </style>

        {/* Fixed structure to prevent double scrollbar */}
        <Flex direction="column" h="100vh">
          {/* Header - Fixed at top - Reduced Size */}
          <Box
            bg="white"
            zIndex={10}
            borderBottom="1px"
            borderColor="gray.100"
            px={4}
            py={3}
            flexShrink={0}
          >
            <Flex align="center" justify="space-between" mb={2}>
              <Box
                bg="gradient-to-r"
                p={1.5}
                borderRadius="lg"
                className="slide-in-sidebar"
              >
                <Image src={logo} alt="Logo" h="55px" />
              </Box>
              <IconButton
                aria-label="Close sidebar"
                icon={<X size={16} />}
                size="xs"
                variant="ghost"
                borderRadius="lg"
                color="gray.600"
                onClick={onClose}
                _hover={{
                  bg: "red.50",
                  color: "red.500",
                  transform: "rotate(90deg)",
                }}
                transition="all 0.3s"
              />
            </Flex>

            {/* Animated Gradient Line */}
            <Box
              h="1.5px"
              className="gradient-animate"
              bgGradient="linear(to-r, blue.400, purple.500, pink.400, blue.400)"
              borderRadius="full"
            />
          </Box>

          {/* Scrollable Menu Items - Takes remaining space */}
          <Box
            flex="1"
            overflowY="auto"
            px={2.5}
            py={2}
            position="relative"
            zIndex={0}
            bg="white"
            sx={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1", // subtle light track
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "linear-gradient(to bottom, #667eea, #764ba2)",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "linear-gradient(to bottom, #5a67d8, #6b46c1)",
              },
            }}
          >
            <VStack spacing={0.5} align="stretch">
              <AnimatePresence>
                {filteredMenu?.map((el, i) => {
                  const Icon = el.icon;
                  const isActive = location.pathname === el.link;

                  return (
                    <MotionFlex
                      key={i}
                      custom={i}
                      initial="hidden"
                      animate="visible"
                      variants={menuItemVariants}
                      align="center"
                      justify="space-between"
                      gap={2}
                      px={2.5}
                      py={2}
                      borderRadius="lg"
                      bg={isActive ? "blue.50" : "transparent"}
                      color={isActive ? "blue.600" : "gray.700"}
                      fontWeight={isActive ? "600" : "500"}
                      cursor="pointer"
                      position="relative"
                      overflow="hidden"
                      border="1px solid"
                      borderColor={isActive ? "blue.200" : "transparent"}
                      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                      _hover={{
                        bg: isActive ? "blue.100" : "gray.50",
                        borderColor: isActive ? "blue.300" : "gray.200",
                        transform: "translateX(3px)",
                        shadow: "sm",
                      }}
                      _active={{
                        transform: "scale(0.98)",
                      }}
                      onClick={() => handleNavigate(el.link)}
                    >
                      {/* Active Indicator Bar */}
                      {isActive && (
                        <Box
                          position="absolute"
                          left={0}
                          top={0}
                          bottom={0}
                          width="3px"
                          bgGradient="linear(to-b, blue.400, purple.500)"
                          borderRadius="0 3px 3px 0"
                        />
                      )}

                      <HStack spacing={2} flex="1">
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          bg={isActive ? "blue.500" : "gray.100"}
                          color={isActive ? "white" : "gray.600"}
                          borderRadius="md"
                          p={1.5}
                          minW="32px"
                          minH="32px"
                          transition="all 0.3s ease"
                          _groupHover={{
                            transform: "scale(1.08)",
                          }}
                        >
                          <Icon size={15} strokeWidth={2.5} />
                        </Box>
                        <Text fontSize="0.8rem" fontWeight="500" noOfLines={1}>
                          {el.text}
                        </Text>
                      </HStack>

                      {/* Badge or Arrow */}
                      {el.badge ? (
                        <Badge
                          colorScheme="purple"
                          fontSize="8px"
                          px={1.5}
                          py={0.25}
                          borderRadius="full"
                          fontWeight="bold"
                        >
                          {el.badge}
                        </Badge>
                      ) : isActive ? (
                        <ChevronRight
                          size={14}
                          style={{
                            opacity: 0.6,
                            transition: "transform 0.3s",
                            flexShrink: 0,
                          }}
                        />
                      ) : null}
                    </MotionFlex>
                  );
                })}
              </AnimatePresence>
            </VStack>
          </Box>

          {/* Footer - Fixed at bottom */}
          <Box
            bg="white"
            borderTop="1px"
            borderColor="gray.100"
            px={4}
            py={2.5}
            flexShrink={0}
          >
            <Box
              p={2.5}
              borderRadius="lg"
              bg="gradient-to-r"
              bgGradient="linear(to-r, blue.50, purple.50)"
              border="1px"
              borderColor="blue.100"
            >
              <Text fontSize="12px" color="gray.600" fontWeight="medium">
                Powered By Matrix CRM
              </Text>
              <Text fontSize="10px" color="gray.500" mt={0.5}>
                © 2025 All rights reserved
              </Text>
            </Box>
          </Box>
        </Flex>
      </DrawerContent>
    </Drawer>
  );
};

export default Sidebar;
